import BigNumber from 'bignumber.js';
// NirmalX pages style their own links (CSS modules), so they use next/link rather than the Chakra-styled Link.
// eslint-disable-next-line no-restricted-imports
import NextLink from 'next/link';
import React from 'react';

import type { Transaction } from 'types/api/transaction';

import config from 'configs/app';
import { WEI } from 'toolkit/utils/consts';

import AddToWallet from '../AddToWallet';
import { ago, shortHash } from '../nirmalx';
import css from './home.module.css';

interface Props {
  txs: Array<Transaction> | undefined;
  isLoading: boolean;
  now: number;
}

const addressLink = (hash: string) => ({ pathname: '/address/[hash]' as const, query: { hash } });

const TxStream = ({ txs, isLoading, now }: Props) => {
  if (isLoading) {
    return <div className={ css.empty }><div className={ css.emptyText }>Loading the latest transactions…</div></div>;
  }

  if (!txs?.length) {
    return (
      <div className={ css.empty }>
        <div className={ css.emptyPulse } aria-hidden="true"><span/><span/><span/></div>
        <div className={ css.emptyTitle }>The first transaction is waiting to be written.</div>
        <p className={ css.emptyText }>
          The { config.chain.name } is live and sealing blocks. Add it to your wallet and send the transaction that
          shows up here first.
        </p>
        <AddToWallet className={ css.ctaPrimary }/>
      </div>
    );
  }

  return (
    <div className={ css.txList }>
      { txs.map((tx) => (
        <div key={ tx.hash } className={ css.txRow }>
          <span className={ `${ css.txStatus } ${ tx.status === 'error' ? css.txFailed : '' }` } title={ tx.status ?? 'pending' }/>
          <NextLink href={{ pathname: '/tx/[hash]', query: { hash: tx.hash } }}>{ shortHash(tx.hash, 10, 6) }</NextLink>
          <span className={ `${ css.txMuted } ${ css.txHide }` }>
            <NextLink href={ addressLink(tx.from.hash) }>{ shortHash(tx.from.hash) }</NextLink>
            { ' → ' }
            { tx.to ? <NextLink href={ addressLink(tx.to.hash) }>{ shortHash(tx.to.hash) }</NextLink> : 'contract creation' }
          </span>
          <span className={ `${ css.txValue } ${ css.txHide }` }>
            { BigNumber(tx.value).dividedBy(WEI).decimalPlaces(4).toFormat() } { config.chain.currency.symbol }
          </span>
          <span className={ css.txMuted }>{ now > 0 ? ago(tx.timestamp, now) : '' }</span>
        </div>
      )) }
    </div>
  );
};

export default React.memo(TxStream);
